import { ReactElement, useCallback, useEffect, useState } from 'react'

import getMFEConfig from 'helpers/getMFEConfig'

import useNavigateOnRedirect from 'hooks/useNavigateOnRedirect'

import { RewardsPage } from 'rewards-mfe'
import {
  AllAccountSummaryGetResponse,
  useAllAccountSummary,
  useCreditAccounts
} from 'const/syfApiQueries'
import { VerticalCardLoader } from 'syf-component-library/ui/atoms'
import { LoaderContainer, MFEFullPageWrapper } from 'pages/subcomponents'
import { useNavigate } from 'react-router-dom'
import routes from 'const/routes'
import { useSetAtom } from 'jotai'
import { selectedAccountAtom } from 'store/atoms'

interface QueryResult<T> {
  data: T[]
  isError: boolean
  isFetched: boolean
}

const defaultResult = {
  data: [],
  isError: false,
  isFetched: true
}

const Rewards = (): ReactElement => {
  useNavigateOnRedirect()

  const setSelectedAccount = useSetAtom(selectedAccountAtom)
  const navigate = useNavigate()
  const [selectedAccountId, setSelectedAccountId] = useState(() =>
    sessionStorage.getItem('selectedAccountId')
  )

  const { data: { accounts = [] } = {} } = useCreditAccounts()
  const {
    data: allAccountSummaryData,
    isError: isAllAccountSummaryError,
    isFetched: isAllAccountSummaryFetched
  } = useAllAccountSummary(accounts).reduce<
    QueryResult<AllAccountSummaryGetResponse>
  >(
    (accountSummary, { data, isError, isFetched }) => ({
      data: [...accountSummary.data, data],
      isError: accountSummary.isError || isError,
      isFetched: accountSummary.isFetched && isFetched
    }),
    defaultResult
  )

  const triggerTJXDeepLinking = useCallback(() => {
    if (isAllAccountSummaryError) {
      navigate(routes.ERROR)
      return
    }

    const tjxAccount = allAccountSummaryData.filter(
      (account) => account?.product?.clientId === 'tjx'
    )
    if (tjxAccount.length === 0) {
      navigate(routes.ERROR)
      return
    }
    const { accountId } = tjxAccount[0]
    setSelectedAccount(accountId)
    setSelectedAccountId(accountId)
    sessionStorage.removeItem('isTJXDeepLinking')
  }, [
    isAllAccountSummaryError,
    allAccountSummaryData,
    setSelectedAccount,
    navigate
  ])

  useEffect(() => {
    if (!accounts?.length) return
    if (
      sessionStorage.getItem('isTJXDeepLinking') === 'true' &&
      isAllAccountSummaryFetched
    ) {
      triggerTJXDeepLinking()
    }
  }, [accounts?.length, isAllAccountSummaryFetched, triggerTJXDeepLinking])

  const mfeConfig = getMFEConfig('mfe')

  return (
    <MFEFullPageWrapper>
      {!selectedAccountId ? (
        <LoaderContainer>
          <VerticalCardLoader />
        </LoaderContainer>
      ) : (
        <RewardsPage config={mfeConfig} />
      )}
    </MFEFullPageWrapper>
  )
}

export default Rewards
